import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { workOrdersApi } from '../services/api'
import { Calendar, ChevronLeft, ChevronRight, Clock, MapPin } from 'lucide-react'
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday } from 'date-fns'
import { it } from 'date-fns/locale'

const STATO_COLORS: Record<string, string> = {
  ricevuto:  '#3b82f6',
  assegnato: '#f59e0b',
  sospeso:   '#6b7280',
  annullato: '#ef4444',
  eseguito:  '#22c55e',
  completato: '#22c55e',
}

type WO = {
  id: number
  numero_progressivo: string
  committente_nome?: string
  denominazione_sito?: string
  cliente_nome?: string
  cliente_indirizzo?: string
  cliente_comune?: string
  ora_apertura?: string | null
  ora_chiusura?: string | null
  stato: string
  tecnico_nome?: string
  data_appuntamento?: string
  ore_appuntamento?: string
  ordine_giro?: number | null
  tipologia?: string
}

const byOrdine = (a: WO, b: WO) => {
  const ao = a.ordine_giro, bo = b.ordine_giro
  if (ao != null && bo != null) return ao - bo
  if (ao != null) return -1
  if (bo != null) return 1
  return (a.ore_appuntamento ?? '').localeCompare(b.ore_appuntamento ?? '')
}

export default function Calendario() {
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selectedDay, setSelectedDay] = useState<Date | null>(new Date())

  const anno = currentMonth.getFullYear()
  const mese = currentMonth.getMonth() + 1

  const { data, isLoading } = useQuery({
    queryKey: ['wo-calendario', anno, mese],
    queryFn: () => workOrdersApi.list({ per_page: 500, anno, mese }).then(r => r.data),
  })

  const pratiche: WO[] = (data?.items ?? []).filter((w: WO) => w.data_appuntamento)

  const days = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  })

  const forDay = (day: Date) =>
    pratiche
      .filter(w => w.data_appuntamento && isSameDay(new Date(w.data_appuntamento), day))
      .sort(byOrdine)

  const selectedPratiche = selectedDay ? forDay(selectedDay) : []

  const startDow = (startOfMonth(currentMonth).getDay() + 6) % 7

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Calendario Interventi</h1>
          <p className="page-subtitle">{pratiche.length} interventi pianificati</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Calendar grid */}
        <div className="lg:col-span-2 card p-4">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setCurrentMonth(m => subMonths(m, 1))} className="btn-ghost px-2 py-1">
              <ChevronLeft size={18} />
            </button>
            <h2 className="font-semibold text-slate-800 capitalize">
              {format(currentMonth, 'MMMM yyyy', { locale: it })}
            </h2>
            <button onClick={() => setCurrentMonth(m => addMonths(m, 1))} className="btn-ghost px-2 py-1">
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(d => (
              <div key={d} className="text-center text-xs font-semibold text-slate-400 py-1">{d}</div>
            ))}
          </div>

          {/* Days */}
          <div className="grid grid-cols-7 gap-0.5">
            {Array.from({ length: startDow }).map((_, i) => (
              <div key={`empty-${i}`} />
            ))}
            {days.map(day => {
              const events = forDay(day)
              const isSelected = selectedDay ? isSameDay(day, selectedDay) : false
              const today = isToday(day)
              return (
                <button
                  key={day.toISOString()}
                  onClick={() => setSelectedDay(day)}
                  className="aspect-square rounded-lg p-1 flex flex-col items-center transition-all hover:bg-slate-100"
                  style={{
                    background: isSelected ? '#1E40AF' : today ? '#EFF6FF' : 'transparent',
                    color: isSelected ? '#fff' : today ? '#1E40AF' : '#374151',
                  }}
                >
                  <span className="text-xs font-semibold">{format(day, 'd')}</span>
                  {events.length > 0 && (
                    <div className="flex gap-0.5 mt-0.5 flex-wrap justify-center">
                      {events.slice(0, 3).map((e, i) => (
                        <span key={i} style={{
                          width: 5, height: 5, borderRadius: '50%',
                          background: isSelected ? 'rgba(255,255,255,0.8)' : STATO_COLORS[e.stato] || '#6b7280',
                          flexShrink: 0,
                        }} />
                      ))}
                      {events.length > 3 && <span style={{ fontSize: 8, color: isSelected ? 'rgba(255,255,255,0.7)' : '#9ca3af' }}>+{events.length - 3}</span>}
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Day detail */}
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={16} className="text-blue-600" />
            <h3 className="font-semibold text-slate-800">
              {selectedDay
                ? format(selectedDay, "d MMMM yyyy", { locale: it })
                : 'Seleziona un giorno'}
            </h3>
          </div>

          {isLoading ? (
            <p className="text-slate-400 text-sm">Caricamento...</p>
          ) : selectedPratiche.length === 0 ? (
            <p className="text-slate-400 text-sm">Nessun intervento pianificato</p>
          ) : (
            <div className="space-y-2">
              {selectedPratiche.map(w => (
                <Link
                  key={w.id}
                  to={`/pratiche/${w.id}`}
                  className="block rounded-lg p-3 border transition-colors"
                  style={{
                    background: w.ora_chiusura && w.ora_chiusura > '14:00' ? '#f0fdf4'
                      : w.ora_chiusura?.startsWith('13:') ? '#fefce8' : undefined,
                    borderColor: w.ora_chiusura && w.ora_chiusura > '14:00' ? '#bbf7d0'
                      : w.ora_chiusura?.startsWith('13:') ? '#fde68a' : '#e2e8f0',
                  }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: STATO_COLORS[w.stato] || '#6b7280',
                      flexShrink: 0,
                    }} />
                    <span className="font-mono text-xs font-semibold text-blue-700">{w.numero_progressivo}</span>
                    {w.ordine_giro != null && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', marginLeft: 'auto' }}>#{w.ordine_giro}</span>
                    )}
                  </div>
                  <div className="text-sm font-medium text-slate-800 truncate">
                    {[w.cliente_indirizzo, w.cliente_comune].filter(Boolean).join(', ') || w.denominazione_sito || w.cliente_nome || '—'}
                  </div>
                  {w.committente_nome && (
                    <div className="text-xs text-slate-400 truncate">{w.committente_nome}</div>
                  )}
                  {(w.ora_apertura || w.ora_chiusura) && (
                    <div className="mt-1">
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                        padding: '1px 6px', borderRadius: 99, fontSize: 10, fontWeight: 600,
                        background: w.ora_chiusura && w.ora_chiusura > '14:00' ? '#dcfce7' : w.ora_chiusura?.startsWith('13:') ? '#fef9c3' : '#f1f5f9',
                        color: w.ora_chiusura && w.ora_chiusura > '14:00' ? '#15803d' : w.ora_chiusura?.startsWith('13:') ? '#92400e' : '#475569',
                        border: `1px solid ${w.ora_chiusura && w.ora_chiusura > '14:00' ? '#bbf7d0' : w.ora_chiusura?.startsWith('13:') ? '#fde68a' : '#cbd5e1'}`,
                      }}>
                        🏛 {w.ora_apertura ?? '?'}–{w.ora_chiusura ?? '?'}
                      </span>
                    </div>
                  )}
                  {w.tecnico_nome && (
                    <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                      <MapPin size={10} />
                      {w.tecnico_nome}
                    </div>
                  )}
                  {w.tipologia && (
                    <div className="text-xs text-slate-400 mt-0.5 capitalize">{w.tipologia.replace(/_/g, ' ')}</div>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upcoming agenda */}
      <div className="card p-4">
        <h3 className="font-semibold text-slate-800 mb-3">Prossimi interventi del mese</h3>
        {pratiche.length === 0 ? (
          <p className="text-slate-400 text-sm">Nessun intervento pianificato per {format(currentMonth, 'MMMM yyyy', { locale: it })}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50/50">
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">Data</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">Pratica</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">Committente</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">Sito</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">Tecnico</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">Orari ufficio</th>
                  <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">Stato</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {pratiche
                  .sort((a, b) => {
                    const dateCmp = (a.data_appuntamento ?? '').localeCompare(b.data_appuntamento ?? '')
                    return dateCmp !== 0 ? dateCmp : byOrdine(a, b)
                  })
                  .map(w => (
                    <tr key={w.id} style={{
                      background: w.ora_chiusura && w.ora_chiusura > '14:00' ? '#f0fdf4'
                        : w.ora_chiusura?.startsWith('13:') ? '#fefce8' : undefined,
                    }}>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">
                        {w.data_appuntamento ? format(new Date(w.data_appuntamento), 'dd/MM', { locale: it }) : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <Link to={`/pratiche/${w.id}`} className="font-mono text-xs font-semibold text-blue-700 hover:underline">
                            {w.numero_progressivo}
                          </Link>
                          {w.ordine_giro != null && (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 99, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>#{w.ordine_giro}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500 max-w-[120px] truncate">{w.committente_nome || '—'}</td>
                      <td className="px-3 py-2 text-xs text-slate-700 max-w-[160px] truncate">{[w.cliente_indirizzo, w.cliente_comune].filter(Boolean).join(', ') || w.denominazione_sito || w.cliente_nome || '—'}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{w.tecnico_nome || '—'}</td>
                      <td className="px-3 py-2">
                        {(w.ora_apertura || w.ora_chiusura) ? (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            padding: '1px 7px', borderRadius: 99, fontSize: 10, fontWeight: 600,
                            background: w.ora_chiusura && w.ora_chiusura > '14:00' ? '#dcfce7' : w.ora_chiusura?.startsWith('13:') ? '#fef9c3' : '#f1f5f9',
                            color: w.ora_chiusura && w.ora_chiusura > '14:00' ? '#15803d' : w.ora_chiusura?.startsWith('13:') ? '#92400e' : '#475569',
                            border: `1px solid ${w.ora_chiusura && w.ora_chiusura > '14:00' ? '#bbf7d0' : w.ora_chiusura?.startsWith('13:') ? '#fde68a' : '#cbd5e1'}`,
                            whiteSpace: 'nowrap',
                          }}>🏛 {w.ora_apertura ?? '?'}–{w.ora_chiusura ?? '?'}</span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 99,
                          fontSize: 10, fontWeight: 600, textTransform: 'capitalize',
                          background: (STATO_COLORS[w.stato] || '#6b7280') + '22',
                          color: STATO_COLORS[w.stato] || '#6b7280',
                        }}>
                          {w.stato}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
